'use client';

/* ──────────────────────────────────────────────
 *  PhoneInput — Simple phone input with country
 *  code prefix. Uses libphonenumber-js for
 *  parsing and formatting (already in monorepo).
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { CountryFlag } from './CountryFlag';

/* ── Country data (computed once) ────────────── */

interface CountryEntry {
  code: CountryCode;
  dialCode: string;
}

const COUNTRY_LIST: CountryEntry[] = getCountries().map(
  (code): CountryEntry => ({ code, dialCode: `+${getCountryCallingCode(code)}` }),
);

const COUNTRY_MAP = new Map<string, CountryEntry>(COUNTRY_LIST.map((c): [string, CountryEntry] => [c.code, c]));

/** Reverse lookup: dial code → country code (longest match first) */
const DIAL_TO_COUNTRY = new Map<string, CountryCode>();
[...COUNTRY_LIST]
  .sort((a, b): number => b.dialCode.length - a.dialCode.length)
  .forEach((c): void => {
    if (!DIAL_TO_COUNTRY.has(c.dialCode)) DIAL_TO_COUNTRY.set(c.dialCode, c.code);
  });

function detectCountryFromDialCode(input: string): CountryCode | undefined {
  const digits = input.replace(/[^\d+]/g, '');
  for (let len = Math.min(digits.length, 5); len >= 2; len--) {
    const prefix = digits.slice(0, len);
    const match = DIAL_TO_COUNTRY.get(prefix);
    if (match) return match;
  }
  return undefined;
}

function detectCountryFromLocale(locale: string): CountryCode {
  const region = locale.split('-')[1]?.toUpperCase();
  if (region && COUNTRY_MAP.has(region)) return region as CountryCode;
  return 'FR';
}

function formatNationalDisplay(value: string, countryCode: CountryCode): string {
  if (!value) return '';
  const parsed = parsePhoneNumberFromString(value);
  if (parsed) return parsed.formatNational().replace(/^0/, '');
  const entry = COUNTRY_MAP.get(countryCode);
  if (entry && value.startsWith(entry.dialCode)) {
    return value.slice(entry.dialCode.length).trim();
  }
  return value.replace(/^\+\d+\s*/, '');
}

/** Localized country names (cached per locale). */
const COUNTRY_NAME_CACHE = new Map<string, Map<string, string>>();

function getCountryNames(locale: string): Map<string, string> {
  const cached = COUNTRY_NAME_CACHE.get(locale);
  if (cached) return cached;
  const names = new Map<string, string>();
  try {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    for (const c of COUNTRY_LIST) {
      names.set(c.code, display.of(c.code) ?? c.code);
    }
  } catch {
    for (const c of COUNTRY_LIST) names.set(c.code, c.code);
  }
  COUNTRY_NAME_CACHE.set(locale, names);
  return names;
}

/* ── Component ──────────────────────────────── */

interface PhoneInputProps {
  value: string;
  onChange: (e164Value: string) => void;
  locale: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  countryCodeLabel?: string;
  placeholder?: string;
  /** Base URL where flag SVGs are hosted (passed down for the embedded widget). */
  flagBaseUrl?: string;
}

export function PhoneInput({
  value,
  onChange,
  locale,
  disabled,
  error,
  id,
  countryCodeLabel,
  placeholder,
  flagBaseUrl,
}: PhoneInputProps) {
  const initialCountry = useMemo((): CountryCode => {
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed?.country) return parsed.country;
    }
    return detectCountryFromLocale(locale);
  }, []);

  const [country, setCountry] = useState<CountryCode>(initialCountry);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * rawInput tracks what the user is actively typing.
   * undefined = use formatted national display from value.
   * string = user is mid-typing (e.g. "+3", "+33 6"), show as-is.
   */
  const [rawInput, setRawInput] = useState<string | undefined>(undefined);

  /* ── Country dropdown state ────────────────── */
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const countryNames = useMemo((): Map<string, string> => getCountryNames(locale), [locale]);

  const filteredCountries = useMemo((): CountryEntry[] => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_LIST;
    return COUNTRY_LIST.filter((c): boolean => {
      const name = countryNames.get(c.code) ?? '';
      return (
        name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.replace('+', '').includes(q.replace('+', ''))
      );
    });
  }, [search, countryNames]);

  /* Close on outside click */
  useEffect((): void | (() => void) => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return (): void => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  /* Focus search when opening */
  useEffect((): void => {
    if (open) {
      setSearch('');
      requestAnimationFrame((): void => searchRef.current?.focus());
    }
  }, [open]);

  const displayValue = rawInput ?? formatNationalDisplay(value, country);

  const handleNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;

      if (!raw.trim()) {
        setRawInput(undefined);
        onChange('');
        return;
      }

      /* ── International mode: user typed + ──────── */
      if (raw.startsWith('+')) {
        /* Full number? */
        const parsed = parsePhoneNumberFromString(raw);
        if (parsed?.country) {
          setCountry(parsed.country);
          setRawInput(undefined); /* switch to formatted display */
          onChange(parsed.format('E.164'));
          return;
        }

        /* Try to detect country from partial dial code (+33, +34, etc.) */
        const detected = detectCountryFromDialCode(raw);
        if (detected) {
          setCountry(detected);
          const dialCode = COUNTRY_MAP.get(detected)?.dialCode ?? '';
          const rest = raw.replace(/[^\d+]/g, '').slice(dialCode.length);
          if (rest) {
            /* Has digits after dial code — try to format */
            const formatter = new AsYouType(detected);
            formatter.input(rest);
            const partial = formatter.getNumber();
            if (partial) {
              setRawInput(undefined);
              onChange(partial.format('E.164'));
            } else {
              setRawInput(undefined);
              onChange(`${dialCode}${rest}`);
            }
          } else {
            /* Just the dial code, no number yet — keep raw visible, clear value */
            setRawInput(raw);
            onChange('');
          }
          return;
        }

        /* Not a recognized dial code yet — keep raw input visible */
        setRawInput(raw);
        return;
      }

      /* ── National mode ─────────────────────────── */
      setRawInput(undefined);
      const digits = raw.replace(/[^\d\s()-]/g, '');
      const formatter = new AsYouType(country);
      formatter.input(digits);
      const parsed = formatter.getNumber();
      if (parsed) {
        onChange(parsed.format('E.164'));
      } else {
        const entry = COUNTRY_MAP.get(country);
        onChange(`${entry?.dialCode ?? ''}${digits.replace(/\D/g, '')}`);
      }
    },
    [country, onChange],
  );

  const handleCountryChange = useCallback(
    (newCountry: CountryCode): void => {
      setCountry(newCountry);
      setRawInput(undefined);
      setOpen(false);
      /* Re-format existing number with new country */
      const currentNational = formatNationalDisplay(value, country);
      if (currentNational) {
        const formatter = new AsYouType(newCountry);
        formatter.input(currentNational);
        const parsed = formatter.getNumber();
        if (parsed) {
          onChange(parsed.format('E.164'));
        } else {
          const entry = COUNTRY_MAP.get(newCountry);
          onChange(`${entry?.dialCode ?? ''}${currentNational.replace(/\D/g, '')}`);
        }
      }
      inputRef.current?.focus();
    },
    [value, country, onChange],
  );

  const dialCode = COUNTRY_MAP.get(country)?.dialCode ?? '';

  return (
    <div className="bt-phone">
      <div className="bt-phone__row">
        <div className="bt-phone__country" ref={dropdownRef}>
          <button
            type="button"
            className={`bt-phone__trigger ${error ? 'bt-field__input--error' : ''}`}
            onClick={(): void => setOpen((v): boolean => !v)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={countryCodeLabel ?? 'Country code'}
          >
            <CountryFlag code={country} baseUrl={flagBaseUrl} />
            <span className="bt-phone__dial-code">{dialCode}</span>
            <svg
              className={`bt-phone__caret ${open ? 'bt-phone__caret--open' : ''}`}
              viewBox="0 0 12 12"
              aria-hidden="true"
            >
              <path
                d="M2.5 4.5 6 8l3.5-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {open && (
            <div className="bt-phone__dropdown" role="listbox">
              <input
                ref={searchRef}
                type="text"
                className="bt-phone__search"
                value={search}
                onChange={(e): void => setSearch(e.target.value)}
                placeholder={countryCodeLabel ?? 'Search'}
              />
              <ul className="bt-phone__list">
                {filteredCountries.map(
                  (c): React.ReactNode => (
                    <li key={c.code}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={c.code === country}
                        className={`bt-phone__option ${c.code === country ? 'bt-phone__option--active' : ''}`}
                        onClick={(): void => handleCountryChange(c.code)}
                      >
                        <CountryFlag code={c.code} baseUrl={flagBaseUrl} />
                        <span className="bt-phone__option-name">{countryNames.get(c.code) ?? c.code}</span>
                        <span className="bt-phone__option-dial">{c.dialCode}</span>
                      </button>
                    </li>
                  ),
                )}
                {filteredCountries.length === 0 && <li className="bt-phone__empty">—</li>}
              </ul>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          id={id}
          type="tel"
          // Clavier numérique et numéro du carnet du navigateur (sans l'indicatif, choisi à côté).
          inputMode="tel"
          autoComplete="tel-national"
          className={`bt-phone__input bt-field__input ${error ? 'bt-field__input--error' : ''}`}
          value={displayValue}
          onChange={handleNumberChange}
          disabled={disabled}
          placeholder={placeholder ?? '6 12 34 56 78'}
        />
      </div>
      {error && <span className="bt-field__error">{error}</span>}
    </div>
  );
}
